import React, { useEffect, useState } from "react";
import { Loader2, Mail, CalendarClock, PhoneCall, Inbox } from "lucide-react";
import { http, formatApiError } from "@/hooks/useAdmin";

const Stat = ({ icon: Icon, label, value, testId }) => (
  <div className="flex items-center gap-3 rounded-xl bg-lightbg px-4 py-3" data-testid={testId}>
    <Icon className="h-4 w-4 text-royal" />
    <div><p className="text-lg font-bold leading-none text-navy">{value}</p><p className="mt-1 text-[11px] font-semibold uppercase tracking-wide text-ink/45">{label}</p></div>
  </div>
);

export const DigestPanel = ({ onUnauthorized }) => {
  const [d, setD] = useState(null);
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    let active = true;
    http.get("/admin/digest/preview")
      .then(({ data }) => { if (active) setD(data); })
      .catch((e) => e.response?.status === 401 && onUnauthorized());
    return () => { active = false; };
  }, [onUnauthorized]);

  const sendNow = async () => {
    setSending(true);
    setMsg("");
    try {
      await http.post("/admin/digest/send");
      const { data } = await http.get("/admin/digest/preview");
      setD(data);
      setMsg(`Sent to ${data.recipient}.`);
    } catch (err) {
      setMsg(formatApiError(err.response?.data?.detail, "Could not send the digest"));
    } finally {
      setSending(false);
    }
  };

  const lastSent = d?.last_sent?.sent_at ? new Date(d.last_sent.sent_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "Not sent yet";

  return (
    <section className="max-w-3xl rounded-2xl border border-navy/[0.08] bg-white p-6" data-testid="digest-panel">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h3 className="font-display text-lg font-bold text-navy">Morning digest</h3>
          <p className="mt-1 text-sm text-ink/55">Every day at 8:30 IST you get one email with today's appointments, due follow-ups and new enquiries{d?.recipient ? ` — sent to ${d.recipient}` : ""}.</p>
        </div>
        <button type="button" data-testid="digest-send-now" onClick={sendNow} disabled={sending || !d}
          className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-navy/15 px-5 py-2.5 text-sm font-bold text-navy hover:bg-lightbg disabled:opacity-50">
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} Send today's digest now
        </button>
      </div>
      {!d ? <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-navy/40" /></div> : (
        <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Stat icon={CalendarClock} label="Appointments today" value={d.appointments.length} testId="digest-stat-appointments" />
          <Stat icon={PhoneCall} label="Follow-ups due" value={d.follow_ups.length} testId="digest-stat-followups" />
          <Stat icon={Inbox} label="New enquiries (24h)" value={d.new_leads.length} testId="digest-stat-leads" />
        </div>
      )}
      <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-ink/50">
        <span data-testid="digest-last-sent">Last sent: {lastSent}</span>
        {msg && <span data-testid="digest-msg" className="font-semibold text-navy">{msg}</span>}
      </div>
    </section>
  );
};
